/**
 * BeaconInputOptions — Coordinator-driven default input settings dialog.
 *
 * Replaces the legacy PlayerInputOptions which depends on PlaycastContext.
 * Reads/writes via beaconCoordinator and peerCoordinator exclusively.
 */

import { faKeyboard, faMouse, faHandBackPointUp, faTimelineArrow } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Dialog,
  DialogCustomContent,
  Label,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@playcastdotio/Shadcn';
import { useEffect, useState } from 'react';
import pcLogo from '../../../../assets/playcast_logo-96x96.png';
import { faGamepadAlt, faGaugeSimpleMax, faXmark } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import { Select } from '../../../components/widgets/Select';
import { beaconCoordinator, peerCoordinator } from '../../../coordinators/init';

export const BeaconInputOptions = () => {
  const [open, setOpen] = useState(false);

  const inputConfig = beaconCoordinator?.getInputConfig();
  const gamepadConfig = peerCoordinator.getGamepadConfig();

  const [autoAssign, setAutoAssign] = useState(gamepadConfig.autoAssign);
  const [maxAssignments, setMaxAssignments] = useState(gamepadConfig.maxAssignments);
  const [keyboardEnabled, setKeyboardEnabled] = useState(inputConfig?.keyboard ?? true);
  const [mouseEnabled, setMouseEnabled] = useState(inputConfig?.mouse ?? true);
  const [touchEnabled, setTouchEnabled] = useState(inputConfig?.touch ?? true);

  useEffect(() => {
    if (!beaconCoordinator) return;
    const store = beaconCoordinator.getBeaconStore();
    const listenerId = store.addListener((state) => {
      setKeyboardEnabled(state.inputConfig.keyboard);
      setMouseEnabled(state.inputConfig.mouse);
      setTouchEnabled(state.inputConfig.touch);
    });
    return () => store.removeListener(listenerId);
  }, []);

  const onOpenChange = () => setOpen((prev) => !prev);

  const handleApply = () => {
    peerCoordinator.updateGamepadConfig({ autoAssign, maxAssignments });
    beaconCoordinator?.setKeyboardEnabled(keyboardEnabled);
    beaconCoordinator?.setMouseEnabled(mouseEnabled);
    beaconCoordinator?.setTouchEnabled(touchEnabled);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <div>
        <Button
          onClick={() => setOpen(true)}
          className='bg-primary theme-midnight:shadow-[inset_2px_3px_2px_#A9E8EA,inset_-2px_-3px_2px_#2D618C] hover:bg-primary/60 focus:bg-midnight active:bg-primary relative m-0 size-full px-2 text-white'
        >
          <FontAwesomeIcon icon={faGamepadAlt} className='text-white' />
          <span className='pl-2'>Default Inputs</span>
        </Button>
      </div>
      <DialogCustomContent className='theme-gray:bg-dark max-w-lg text-white sm:min-w-[425px]'>
        <div className='bg-primary z-50 flex w-full items-center rounded-t-lg shadow-sm'>
          <div className='ml-4 flex shrink-0 items-center gap-x-2'>
            <img src={pcLogo} alt='Logo' className='h-7 w-7' />
            <h1 className='text-md font-headline font-semibold text-white'>Default Input Settings</h1>
          </div>
          <div className='flex-grow' />
          <Button
            size='icon'
            variant='ghost'
            className='h-10 w-9 rounded-t-lg text-gray-300 hover:bg-inherit hover:text-white'
            onClick={onOpenChange}
          >
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </div>
        <div className='flex flex-col gap-10 p-6 pb-0'>
          <div className='flex flex-col gap-6'>
            <div className='border-primary rounded-md border-2 p-2'>
              <div className='flex gap-4'>
                <div className='flex items-center justify-center text-center'>
                  <FontAwesomeIcon
                    icon={faTimelineArrow}
                    className={clsx(autoAssign ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                  />
                </div>
                <Label htmlFor='beacon-gamepad-auto' className='text-md flex flex-1 cursor-pointer font-light'>
                  Assign Gamepad Automatically
                </Label>
                <Switch
                  id='beacon-gamepad-auto'
                  checked={autoAssign}
                  onCheckedChange={() => setAutoAssign((prev) => !prev)}
                  className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
                />
              </div>
              {autoAssign && (
                <div className='theme-midnight:bg-gray-800 bg-darker flex rounded-b-lg p-2'>
                  <div className='flex items-center justify-center text-center'>
                    <FontAwesomeIcon icon={faGaugeSimpleMax} className='h-6 w-6 text-emerald-400' />
                    <TooltipProvider>
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                          <Label htmlFor='beacon-max-assignments' className='text-md ml-4 flex flex-1 cursor-pointer font-light'>
                            Max Gamepads
                          </Label>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>When enabled, new players are automatically assigned the next available controller slot.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select
                    selected={maxAssignments.toString()}
                    onChange={(e) => setMaxAssignments(parseInt(e))}
                    className='ml-auto w-auto rounded-md bg-gray-700 p-2 text-white shadow-xl'
                    data={[
                      { value: '0', label: '0' },
                      { value: '1', label: '1' },
                      { value: '2', label: '2' },
                      { value: '3', label: '3' },
                      { value: '4', label: '4' },
                    ]}
                  />
                </div>
              )}
            </div>

            <div className='border-primary flex gap-4 rounded-md border-2 p-2'>
              <div className='flex items-center justify-center text-center'>
                <FontAwesomeIcon
                  icon={faKeyboard}
                  className={clsx(keyboardEnabled ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                />
              </div>
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Label htmlFor='beacon-keyboard-toggle' className='text-md flex flex-1 cursor-pointer font-light'>
                      Share Keyboard by Default
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>When enabled, new players will be allowed to use your keyboard.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Switch
                id='beacon-keyboard-toggle'
                checked={keyboardEnabled}
                onCheckedChange={() => setKeyboardEnabled((prev) => !prev)}
                className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
              />
            </div>

            <div className='border-primary flex gap-4 rounded-md border-2 p-2'>
              <div className='flex items-center justify-center text-center'>
                <FontAwesomeIcon
                  icon={faMouse}
                  className={clsx(mouseEnabled ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                />
              </div>
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Label htmlFor='beacon-mouse-toggle' className='text-md flex flex-1 cursor-pointer font-light'>
                      Share Mouse by Default
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>When enabled, new players will be allowed to use your mouse.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Switch
                id='beacon-mouse-toggle'
                checked={mouseEnabled}
                onCheckedChange={() => setMouseEnabled((prev) => !prev)}
                className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
              />
            </div>

            <div className='border-primary flex gap-4 rounded-md border-2 p-2'>
              <div className='flex items-center justify-center text-center'>
                <FontAwesomeIcon
                  icon={faHandBackPointUp}
                  className={clsx(touchEnabled ? 'text-emerald-400' : 'text-gray-400', 'h-6 w-6')}
                />
              </div>
              <TooltipProvider>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Label htmlFor='beacon-touch-toggle' className='text-md flex flex-1 cursor-pointer font-light'>
                      Share Touch by Default
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>When enabled, new players will be allowed to use touch input.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Switch
                id='beacon-touch-toggle'
                checked={touchEnabled}
                onCheckedChange={() => setTouchEnabled((prev) => !prev)}
                className='ml-auto data-[state=checked]:bg-emerald-400 data-[state=unchecked]:bg-gray-700 [&>span]:bg-white'
              />
            </div>
          </div>
        </div>
        <div className='flex flex-1 justify-end p-4'>
          <Button onClick={handleApply} className='m-0 w-fit bg-amber-400 px-8 hover:opacity-90'>
            Apply
          </Button>
        </div>
      </DialogCustomContent>
    </Dialog>
  );
};
